/**
 * apps/web/src/lib/api.ts
 *
 * Typed fetch wrappers for all backend API routes.
 * All functions throw on non-ok responses.
 */

import type {
  Agent,
  AgentStatus,
  Campaign,
  Lead,
  CallLog,
  PaginatedResponse,
  Disposition,
} from '../types';

const envBase = import.meta.env.VITE_API_BASE_URL;
// Ensure that if a base URL is provided, it always includes the /api prefix
export const BASE = envBase ? (envBase.endsWith('/api') ? envBase : `${envBase.replace(/\/$/, '')}/api`) : '/api';


async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
  });

  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
      throw new Error('Unauthorized: Session expired');
    }

    let errorMessage = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      errorMessage = body.error || errorMessage;
    } catch (e) {
      // ignore JSON parse error
    }
    throw new Error(errorMessage);
  }

  return res.json() as Promise<T>;
}

// ── Agents ──────────────────────────────────────────────────

export const api = {
  agent: {
    getCallerId: () =>
      request<{ callerId: string }>('/agent/caller-id').then((r) => r.callerId),
    getProfile: () =>
      request<any>('/agent/me').then((r) => r?.data || r?.agent || r),
    getCredits: () =>
      request<any>('/agent/credits').then((r) => {
        const creds = r?.credits ?? r?.data?.credits ?? r?.data?.remaining_balance ?? 0;
        const allocated = r?.allocated ?? r?.data?.allocated_credits ?? 0;
        const spent = r?.spent ?? r?.data?.spent_credits ?? 0;
        return { credits: Number(creds), allocated: Number(allocated), spent: Number(spent) };
      }),
    status: () =>
      request<{ status: AgentStatus; changed_at: string }>('/agent/status'),
    setStatus: (status: AgentStatus) =>
      request<{ success: boolean; status: AgentStatus }>('/agent/status', {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
  },

  agents: {
    list: () =>
      request<{ data: Agent[] }>('/agents').then((r) => r.data),

    get: (id: string) =>
      request<{ data: Agent }>(`/agents/${id}`).then((r) => r.data),

    updateStatus: (id: string, status: AgentStatus) =>
      request<{ data: Agent }>(`/agents/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }).then((r) => r.data),

    webrtcToken: (id: string) =>
      request<{ token?: string; error?: string; status?: number }>(`/agents/${id}/webrtc-token`, {
        method: 'POST',
      }).then((r) => r),

    create: (data: { name: string; email: string; telnyx_credential_id?: string; telnyx_sip_username?: string }) =>
      request<{ data: Agent }>('/agents', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => r.data),
  },

// ── Admin ───────────────────────────────────────────────────

  admin: {
    getAgents: () =>
      request<any>('/admin/agents').then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r?.agents) ? r.agents : (Array.isArray(r) ? r : []))),

    allocateToAgent: (agentId: string, credits: number, phoneNumber?: string | null) =>
      request<{ success: boolean; message?: string; transferred?: number; newAvailableBalance?: number }>(`/admin/agents/${agentId}/allocate`, {
        method: 'POST',
        body: JSON.stringify({ credits, phoneNumber }),
      }),

    reclaimFromAgent: (agentId: string, amount: number) =>
      request<{ success: boolean; reclaimed: number; message?: string }>(`/admin/agents/${agentId}/reclaim`, {
        method: 'POST',
        body: JSON.stringify({ amount }),
      }),

    deleteAgent: (id: string) =>
      request<{ success: boolean; deleted_agent_id: string }>(`/admin/agents/${id}`, {
        method: 'DELETE',
      }),

    resetAgentPassword: (id: string) =>
      request<{ success: boolean; new_password: string }>(`/admin/agents/${id}/reset-password`, {
        method: 'POST',
      }),

    getAgentStatus: () =>
      request<any>('/admin/agent-status').then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r?.agents) ? r.agents : (Array.isArray(r) ? r : []))),

    getAgentStatuses: () =>
      request<any>('/admin/agents/status').then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r?.agents) ? r.agents : (Array.isArray(r) ? r : []))),

    getWorkSummary: () =>
      request<any>('/admin/agents/work-summary').then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : [])),

    getCallRecordings: (agentId?: string, date?: string) => {
      const qs = new URLSearchParams();
      if (agentId) qs.set('agent_id', agentId);
      if (date) qs.set('date', date);
      const query = qs.toString();
      return request<any>(`/admin/call-recordings${query ? `?${query}` : ''}`).then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r?.recordings) ? r.recordings : (Array.isArray(r) ? r : [])));
    },

    getRecordings: (agentId?: string, date?: string) => {
      const qs = new URLSearchParams();
      if (agentId) qs.set('agent_id', agentId);
      if (date) qs.set('date', date);
      const query = qs.toString();
      return request<any>(`/admin/recordings${query ? `?${query}` : ''}`).then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r?.recordings) ? r.recordings : (Array.isArray(r) ? r : [])));
    },

    getPhoneNumbers: () =>
      request<any>('/admin/phone-numbers').then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r?.phone_numbers) ? r.phone_numbers : (Array.isArray(r) ? r : []))),

    createAgent: (data: { username: string; password: string }) =>
      request<{ data: any }>('/admin/agents', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => r.data),

    uploadLeads: (assigned_user_id: string | null, file_name: string, leads: any[], assignment_mode: string = 'assigned') =>
      request<{ batch_id: string; inserted: number; skipped: number; errors: string[] }>('/admin/leads/upload', {
        method: 'POST',
        body: JSON.stringify({ assigned_user_id, file_name, leads, assignment_mode }),
      }),

    getBatches: () =>
      request<any>('/admin/leads/batches').then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : [])),

    deleteBatch: (id: string) =>
      request<{ deleted_batch_id: string }>(`/admin/leads/batch/${id}`, {
        method: 'DELETE',
      }),
      
    deleteLead: (id: string) =>
      request<{ deleted_lead_id: string }>(`/admin/leads/${id}`, {
        method: 'DELETE',
      }),
      
    getLeads: (batchId?: string) =>
      request<any>(`/admin/leads${batchId ? `?batch_id=${batchId}` : ''}`).then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : [])),

    assignLead: (lead_id: string, user_id: string | null) =>
      request<{ success: boolean }>('/admin/leads/assign', {
        method: 'PATCH',
        body: JSON.stringify({ lead_id, user_id }),
      }),

    assignBulkLeads: (lead_ids: string[], user_id: string | null) =>
      request<{ success: boolean; count: number }>('/admin/leads/assign-bulk', {
        method: 'PATCH',
        body: JSON.stringify({ lead_ids, user_id }),
      }),

    distributeLeadsRandomly: (lead_ids: string[], user_ids: string[]) =>
      request<{ success: boolean; count: number }>('/admin/leads/distribute-randomly', {
        method: 'PATCH',
        body: JSON.stringify({ lead_ids, user_ids }),
      }),

    getNumbers: () =>
      request<any>('/admin/numbers').then((r) => Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : [])),

    assignNumber: (phone_id: string, user_id: string | null) =>
      request<{ success: boolean }>('/admin/numbers/assign', {
        method: 'POST',
        body: JSON.stringify({ phone_id, user_id }),
      }),

    getTenant: () =>
      request<any>('/admin/tenant').then((r) => r?.data || r || {}),

    getDashboard: () =>
      request<any>('/admin/dashboard').then((r) => r?.data || r || {}),
  },

  // ── Campaigns ────────────────────────────────────────────

  campaigns: {
    list: () =>
      request<{ data: Campaign[] }>('/campaigns').then((r) => r.data),

    get: (id: string) =>
      request<{ data: Campaign }>(`/campaigns/${id}`).then((r) => r.data),
  },

  // ── Leads ────────────────────────────────────────────────

  leads: {
    list: (params?: { campaign_id?: string; status?: string; page?: number; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.campaign_id) qs.set('campaign_id', params.campaign_id);
      if (params?.status) qs.set('status', params.status);
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      return request<PaginatedResponse<Lead>>(`/leads?${qs}`);
    },
    updateStatus: (id: string, status: string) =>
      request<{ data: Lead }>(`/leads/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }).then((r) => r.data),
  },

  // ── Calls ────────────────────────────────────────────────

  calls: {
    list: (params?: {
      agent_id?: string;
      campaign_id?: string;
      telnyx_call_control_id?: string;
      status?: string;
      page?: number;
      limit?: number;
    }) => {
      const qs = new URLSearchParams();
      if (params?.agent_id) qs.set('agent_id', params.agent_id);
      if (params?.campaign_id) qs.set('campaign_id', params.campaign_id);
      if (params?.telnyx_call_control_id) qs.set('telnyx_call_control_id', params.telnyx_call_control_id);
      if (params?.status) qs.set('status', params.status);
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      return request<PaginatedResponse<CallLog>>(`/calls?${qs}`);
    },

    get: (id: string) =>
      request<{ data: CallLog }>(`/calls/${id}`).then((r) => r.data),

    getByControlId: (callControlId: string) =>
      request<PaginatedResponse<CallLog>>(
        `/calls?telnyx_call_control_id=${encodeURIComponent(callControlId)}`,
      ).then((r) => r.data[0] ?? null),

    submitDisposition: (id: string, disposition: Disposition, notes?: string) =>
      request<{ success: boolean }>(`/calls/${id}/disposition`, {
        method: 'POST',
        body: JSON.stringify({ disposition, notes }),
      }),

    logManual: (data: {
      agentId: string;
      phoneNumber: string;
      leadId?: string;
      campaignId?: string;
      telnyx_call_control_id?: string;
      direction?: 'outbound' | 'inbound';
    }) =>
      request<{ data: CallLog }>('/calls/manual', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => r.data),

    hangup: (id: string) =>
      request<{ success: boolean }>(`/calls/${id}/hangup`, { method: 'POST' }),

    outbound: (to: string) =>
      request<{ success: boolean; data: any }>('/calls/outbound', {
        method: 'POST',
        body: JSON.stringify({ to }),
      }),
      
    update: (id: string, data: { status?: string; end_time?: string; duration?: number; hangup_cause?: string; setup_duration_ms?: number; failure_category?: string }) =>
      request<{ data: CallLog }>(`/calls/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }).then((r) => r.data),
  },

  // ── Callbacks ────────────────────────────────────────────

  callbacks: {
    list: (params?: { status?: string; agent_id?: string; due_only?: boolean }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.agent_id) qs.set('agent_id', params.agent_id);
      if (params?.due_only) qs.set('due_only', 'true');
      const query = qs.toString();
      return request<{ data: any[] }>(`/callbacks${query ? `?${query}` : ''}`).then((r) => r.data);
    },

    create: (data: {
      lead_id?: string;
      phone_number: string;
      contact_name?: string;
      scheduled_time: string;
      assigned_agent_id?: string;
      notes?: string;
    }) =>
      request<{ success: boolean; data: any }>('/callbacks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: { status?: 'pending' | 'completed' | 'dismissed'; scheduled_time?: string; notes?: string }) =>
      request<{ data: any }>(`/callbacks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }).then((r) => r.data),

    delete: (id: string) =>
      request<{ success: boolean; id: string }>(`/callbacks/${id}`, {
        method: 'DELETE',
      }),
  },

  // ── Messages (2-Way SMS) ─────────────────────────────────

  messages: {
    list: (params?: { phone_number?: string; limit?: number; page?: number }) => {
      const qs = new URLSearchParams();
      if (params?.phone_number) qs.set('phone_number', params.phone_number);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.page) qs.set('page', String(params.page));
      const query = qs.toString();
      return request<{ data: any[]; total: number; page: number; limit: number }>(`/messages${query ? `?${query}` : ''}`);
    },

    send: (data: { to: string; body: string; from?: string }) =>
      request<{ success: boolean; data: any }>('/messages', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
};
